import {MAX_FRACTION_DIGITS_LIMIT} from "./numberConstants";

export type FormatNumberOptions = Omit<Intl.NumberFormatOptions, "style"> & {
  style?: Intl.NumberFormatPartTypes | "percent";
  locale?: string;
};

/**
 * A higher-order function that uses Intl.NumberFormat behind the scenes to format a number
 * @param {Object} providedOptions -
 * @param {string} providedOptions.locale - Passed as first argument to the Intl.NumberFormat. Defaults to one of [navigator.language, "en-GB"] or respects provided locale from the call-site
 * @returns {function} Function
 */
function formatNumber(providedOptions: FormatNumberOptions = {}) {
  const {locale, ...otherOptions} = providedOptions;
  let options = otherOptions;
  const isCurrencyFormatting = options.style === "currency";
  const isCompactNotation = options.notation === "compact";

  if (isCompactNotation) {
    options = {
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
      ...otherOptions
    };
  } else if (isCurrencyFormatting) {
    options = {
      currency: "USD",
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
      // "narrowSymbol" prevents showing explicit currency symbols, such as "US$" or "CA$"
      currencyDisplay: "narrowSymbol",
      ...otherOptions
    };
  } else {
    options = {
      maximumFractionDigits: 5,
      minimumFractionDigits: 2,
      style: "decimal",
      ...otherOptions
    };
  }

  let numberFormatter: {
    format: (x: number | bigint) => string;
  };

  try {
    numberFormatter = new Intl.NumberFormat(locale || "en-US", options);
  } catch (error) {
    numberFormatter = {
      format(x: number | bigint) {
        return x.toLocaleString();
      }
    };

    if (isCurrencyFormatting) {
      // currencyDisplay: "narrowSymbol" option is not supported by some browsers, try by providing "symbol"
      try {
        options.currencyDisplay = "symbol";
        numberFormatter = new Intl.NumberFormat(locale || "en-US", options);
      } catch (currencyFormattingError) {
        console.error({currencyFormattingError});
      }
    }
  }

  return (value: number) => {
    let formattedValue = "";

    if (!Object.is(value, NaN)) {
      formattedValue = numberFormatter.format(value);
    }

    return formattedValue;
  };
}

function isFiniteNumber(x: unknown): x is number {
  return typeof x === "number" && Number.isFinite(x);
}

/**
 * Parses value into a float
 */
function parseAsFloat(value: unknown, fallback?: number) {
  const fallbackValue = typeof fallback === "number" ? fallback : NaN;

  return typeof value === "string" ? parseFloat(value) : fallbackValue;
}

function isPositiveNumber(value: unknown) {
  return typeof value === "number" && isFiniteNumber(value) && value > 0;
}

/**
 * Rounds a number up to the provided decimal places limit
 * @param {Object} options -
 * @param {number} x -
 * @returns {number} Rounded number
 */
function roundNumber({decimalPlaces = 0}, x: number): number {
  // eslint-disable-next-line prefer-template
  return Number(Math.round(Number(x + `e+${decimalPlaces}`)) + `e-${decimalPlaces}`);
}

/**
 * @param {number} percent - Percent value
 * @returns {number} Decimal representation of the percent value
 */
function percentToDecimal(percent: number): number {
  // eslint-disable-next-line no-magic-numbers
  return percent / 100;
}

/**
 *
 * @param {number} decimal - Decimal value
 * @returns Percent representation of the decimal value
 */
function decimalToPercent(decimal: number): number {
  // eslint-disable-next-line no-magic-numbers
  return decimal * 100;
}

/**
 * Converts numbers into strings
 *
 * - Makes sure (1e-2) kinda values don't end up with "1e-2", but instead it would return "0.01"
 *
 * @example
 * convertToStringRepresentation(1e-2); // "0.01"
 * convertToStringRepresentation(1e2); // "100"
 * convertToStringRepresentation(28); // "28"
 */
function convertToStringRepresentation(number: number): string {
  return number.toLocaleString("en-US", {
    maximumFractionDigits: MAX_FRACTION_DIGITS_LIMIT,
    useGrouping: false
  });
}

/**
 * Can be used for the cases where we want to use default formatting for usd prices,
 * which uses fixed number of decimal places depending on the price value, for consistency;
 * - 2 decimal places for prices >= 1 (e.g. `12,344.00` or `2.45`)
 * - min 2, max 6 decimal places for prices < 1 (e.g. `0.010002` or `0.05`)
 * @returns the default minimum and maximum fraction digits for the USD price formatter.
 */
function getUSDFormatterFractionDigits(
  price: number
): Pick<FormatNumberOptions, "maximumFractionDigits" | "minimumFractionDigits"> {
  if (price < 1) {
    return {
      minimumFractionDigits: 2,
      maximumFractionDigits: 6
    };
  }

  return {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  };
}

/**
 * @returns formatted (using the default fraction digits) price
 */
function formatUSDPrice(value: number) {
  return formatPrice(getUSDFormatterFractionDigits(value))(value);
}

function formatPrice(options?: FormatNumberOptions) {
  const {style = "currency", currency = "USD", ...otherOptions} = options || {};

  const formatter = formatNumber({
    style,
    currency,
    ...otherOptions
  });

  return (value: number) => {
    let formattedValue = formatter(value);

    if (formattedValue && currency === "USD") {
      // in case `narrowSymbol` option was failed, make sure "US$" sign appears as just "$"
      formattedValue = formattedValue.replace("US$", "$");
    }

    return formattedValue;
  };
}

export {
  formatNumber,
  parseAsFloat,
  isPositiveNumber,
  percentToDecimal,
  decimalToPercent,
  roundNumber,
  convertToStringRepresentation,
  isFiniteNumber,
  getUSDFormatterFractionDigits,
  formatUSDPrice,
  formatPrice
};
